import { useQueryClient } from 'react-query';
import { useToastify } from '../../../../../../Hooks';
import useFormServerErrors from '../../../../../../Hooks/useFormServerErrors';
import {
    useFrontendKendoGridDataHandler,
    useKendoGrid,
    useKendoGridInlineEditing,
} from '../../../../../../Hooks/useKendoGrid';
import { trimStrings } from '../../../../../Licenses/licenseHelper';
import {
    useCreatePlaceholderManagement,
    useDeletePlaceholderManagement,
    useUpdatePlaceholderManagement,
} from '../../hooks/usePlaceholderManagement';

const idField = 'id';
const editField = 'inEdit';

const prepareData = (data: any) => ({
    ...data,
    targetFolder: data.targetFolder?.fullPath,
    workpackage: data.workpackage?.asiteCustomAttributeCode,
    organisation: data.organisation?.asiteCustomAttributeCode,
    documentType: data.documentType?.asiteCustomAttributeCode,
    phase: data.phase?.asiteCustomAttributeCode,
    deelprojectId: data.deelprojectId?.id,
    targetWorkspaceName: data.targetFolder?.workspaceName,
});

const usePlaceholderManagement = (dgs: any) => {
    const queryClient = useQueryClient();
    const { gridState, gridResult, setGridState, changeGridResult } = useKendoGrid({
        source: dgs as any[],
    });
    const handleDataStateChange = useFrontendKendoGridDataHandler({
        setGridState,
        changeGridResult,
        data: dgs as any[],
    });
    const { showToastError, showToastSuccess } = useToastify();
    const { addNew, add, discard, enterEdit, update, cancel, remove, handleItemChange } = useKendoGridInlineEditing({
        idField,
        editField,
        source: dgs as any[],
        data: gridResult.data,
        setData: changeGridResult,
    });

    const createPlaceholderManagementMutation = useCreatePlaceholderManagement();
    const deletePlaceholderManagementMutation = useDeletePlaceholderManagement();
    const updatePlaceholderManagementMutation = useUpdatePlaceholderManagement();
    const { mutate: createPlaceholderManagement } = createPlaceholderManagementMutation;
    const { mutate: deletePlaceholderManagement } = deletePlaceholderManagementMutation;
    const { mutate: updatePlaceholderManagement } = updatePlaceholderManagementMutation;
    const { hasError, getCustomErrorMessageProductReservation } = useFormServerErrors();
    const addItem = (dataItem: any) => {
        const model = {
            ...prepareData(dataItem),
            targetFolderId: dataItem.targetFolder?.folderId,
        };
        trimStrings(model);
        delete model?.id;
        createPlaceholderManagement(model, {
            onSuccess(response: any) {
                if (hasError(response)) {
                    showToastError(getCustomErrorMessageProductReservation(response));
                } else {
                    showToastSuccess('Placeholder added successfully');
                    add(dataItem);
                    queryClient.invalidateQueries(['placeholders']);
                }
            },
            onError(error: any) {
                showToastError(getCustomErrorMessageProductReservation(error?.response?.data));
            },
        });
    };

    const deleteItem = (dataItem: any) => {
        deletePlaceholderManagement(dataItem[idField], {
            onSuccess(response: any) {
                if (hasError(response)) {
                    showToastError(getCustomErrorMessageProductReservation(response));
                } else {
                    showToastSuccess('Placeholder deleted');
                    remove(dataItem);
                    queryClient.invalidateQueries(['placeholders']);
                }
            },
            onError(error: any) {
                showToastError(getCustomErrorMessageProductReservation(error?.response?.data));
            },
        });
    };

    const updateItem = (dataItem: any) => {
        delete dataItem.inEdit;
        const model = {
            ...prepareData(dataItem),
            targetFolderId: dataItem.targetFolder?.folderId ?? dataItem?.targetFolderId,
        };
        trimStrings(model);
        updatePlaceholderManagement(model, {
            onSuccess(response: any) {
                if (hasError(response)) {
                    showToastError(getCustomErrorMessageProductReservation(response));
                } else {
                    showToastSuccess('Placeholder updated successfully');
                    update(dataItem);
                    queryClient.invalidateQueries(['placeholders']);
                }
            },
            onError(error: any) {
                showToastError(getCustomErrorMessageProductReservation(error?.response?.data));
                cancel(dataItem);
            },
        });
    };
    const validate = (dataItem: any) => {
        if (!dataItem.targetFolder) {
            showToastError('Target folder is required');
            return false;
        }
        if (!dataItem.deelprojectId) {
            showToastError('Deelproject is required');
            return false;
        }
        if (!dataItem.organisation) {
            showToastError('Organisation is required');
            return false;
        }

        return true;
    };

    return {
        idField,
        editField,
        gridState,
        gridResult,
        setGridState,
        changeGridResult,
        handleDataStateChange,
        handleItemChange,
        validate,
        inlineEditing: { addNew, add: addItem, discard, enterEdit, update: updateItem, cancel, remove: deleteItem },
        mutations: {
            createPlaceholderManagementMutation,
            deletePlaceholderManagementMutation,
        },
    };
};

export default usePlaceholderManagement;
